import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const FREQ_LABELS = {
  weekly: 'Weekly (every week)',
  biweekly: 'Every 2 weeks',
  triweekly: 'Every 3 weeks',
  fortweekly: 'Every 4 weeks',
  monthly: 'Monthly',
  bimonthly: 'Every 2 months',
  'one-time': 'One-time service',
  custom: 'Custom / as needed',
}

const SERVICE_LABELS = {
  standard: 'Standard Cleaning',
  deep: 'Deep Clean',
  airbnb: 'Airbnb Turnover',
  'move-out': 'Move-Out Cleaning',
  mold: 'Mold Treatment',
  custom: 'Custom',
}

const blankForm = (client) => ({
  service_type: client.is_airbnb ? 'airbnb' : 'standard',
  frequency: client.frequency || 'biweekly',
  rate: client.default_rate || '',
  rate_type: 'flat',
  scope_notes: '',
  allow_social_media: false,
})

function generateWorkOrderPDF({ client, form }) {
  const TERMS = [
    ['💰 Payment', `Payment is due upon completion of each service. ${form.rate_type === 'hourly' ? 'The rate listed above is the agreed hourly rate, charged for the time spent cleaning on each visit.' : 'The rate listed above is the agreed flat rate per visit.'} Any additional services requested on the day of cleaning that fall outside the original scope may be subject to additional charges, which will be communicated before proceeding.`],
    ['📸 Photo Documentation', `Before and after photos may be taken during cleanings as proof of service and quality assurance. ${form.allow_social_media ? "You have agreed to allow Kitty's Cleaning Services to use select photos on social media. We will blur or remove any personal or identifying information, never photograph bedrooms, and only share kitchens, bathrooms, and common areas." : 'You have opted out of social media use. Any photos will only be used internally.'}`],
    ['⚖️ Dispute Resolution', `Any service disputes must be raised within 24 hours of the cleaning with specific details. Claims made after 24 hours without prior notice cannot be honored.`],
    ['🔑 Property Access', `Client agrees to provide necessary access to the property on scheduled cleaning days. If access is not provided and the team is unable to complete the service, the visit may be charged at the full rate.`],
    ['➕ Changes to Scope', `Any changes to the agreed scope of service (additional rooms, tasks, or frequency) must be communicated in advance. Day-of additions outside the original scope are subject to additional charges.`],
    ['🛡️ Liability', `Kitty's Cleaning Services carries general liability insurance. In the unlikely event of damage, the client must notify us within 24 hours with photos. We will work promptly to resolve any legitimate claims.`],
    ['🚫 Right to Refuse', `Kitty's Cleaning Services reserves the right to refuse or reschedule service if the property is found to be in an unsafe condition or contains hazards not previously disclosed, if the requested work changes from the agreed scope, or if a signed work order is not on file.`],
    ['✍️ Electronic Signature', `By signing electronically, the client agrees that their drawn or typed signature has the same legal effect as a handwritten signature under the California Uniform Electronic Transactions Act (Civil Code section 1633.1 and following).`],
    ['🏠 Authority to Sign', `The person signing confirms they are the owner of the property, or are authorized by the owner, to hire Kitty's Cleaning Services for this property and agree to these terms.`],
  ]
  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Work Order — ${client.nickname||client.name}</title>
<style>
* { box-sizing:border-box; margin:0; padding:0; }
body { font-family:Arial,sans-serif; padding:40px; color:#202124; max-width:720px; margin:0 auto; background:#fff; }
@media print {
  .pink-bg { background:#e8547a !important; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  .light-pink { background:#fdf0f3 !important; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  body { padding:24px; }
}
.header-bar { background:#e8547a; padding:22px 28px; border-radius:10px 10px 0 0; }
.co-name { color:#fff; font-size:22px; font-weight:700; letter-spacing:.5px; }
.co-sub { color:rgba(255,255,255,.8); font-size:11px; margin-top:3px; line-height:1.6; }
.doc-title { color:#fff; font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; margin-top:10px; }
.body { padding:26px 28px; border:2px solid #e8547a; border-top:none; border-radius:0 0 10px 10px; }
.sec-label { font-size:9px; text-transform:uppercase; letter-spacing:.12em; color:#e8547a; font-weight:700; margin-bottom:6px; }
.client-name { font-size:18px; font-weight:700; margin-bottom:4px; }
.client-detail { font-size:12px; color:#5f6368; line-height:1.7; margin-bottom:20px; }
.detail-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:16px; margin-bottom:20px; padding-bottom:20px; border-bottom:1.5px solid #f0d0da; }
.detail-item { font-size:12px; }
.detail-item strong { display:block; font-size:9px; text-transform:uppercase; letter-spacing:.1em; color:#e8547a; margin-bottom:3px; }
.scope-box { background:#fdf0f3; border-left:3px solid #e8547a; border-radius:0 8px 8px 0; padding:12px 14px; margin-bottom:20px; }
.scope-label { font-size:9px; text-transform:uppercase; letter-spacing:.1em; color:#e8547a; font-weight:700; margin-bottom:5px; }
.scope-text { font-size:12px; color:#5f6368; line-height:1.6; }
.social-row { display:flex; gap:10px; align-items:flex-start; font-size:11px; color:#5f6368; margin-bottom:24px; line-height:1.6; }
.checkbox { width:14px; height:14px; border:1.5px solid #e8547a; border-radius:3px; flex-shrink:0; margin-top:1px; text-align:center; font-size:11px; font-weight:700; color:#e8547a; line-height:13px; }
.terms-section { margin-bottom:20px; }
.terms-title { font-size:12px; font-weight:700; color:#e8547a; text-transform:uppercase; letter-spacing:.08em; margin-bottom:12px; padding-bottom:6px; border-bottom:1.5px solid #f0d0da; }
.term-item { margin-bottom:11px; }
.term-title { font-size:12px; font-weight:700; color:#202124; margin-bottom:3px; }
.term-text { font-size:11px; color:#5f6368; line-height:1.6; }
.sign-section { margin-top:30px; padding-top:20px; border-top:1.5px dashed #bdc1c6; }
.sign-row { display:flex; gap:30px; margin-bottom:24px; }
.sign-field { flex:1; }
.sign-line { border-bottom:1.5px solid #202124; height:32px; }
.sign-field-label { font-size:10px; color:#80868b; margin-top:4px; text-transform:uppercase; letter-spacing:.08em; }
.footer { text-align:center; padding-top:16px; margin-top:10px; border-top:1px solid #f0d0da; }
.footer-main { font-size:12px; color:#e8547a; font-weight:600; margin-bottom:4px; }
.footer-sub { font-size:10px; color:#9aa0a6; line-height:1.7; }
</style></head><body>
<div class="header-bar pink-bg">
  <div class="co-name">Kitty's Cleaning Services 🐈</div>
  <div class="co-sub">6427 Wilcox Ave, Bell, CA 90201 &nbsp;·&nbsp; (310) 425-3195 &nbsp;·&nbsp; info@kittyscleaningservices.com</div>
  <div class="doc-title">Work Order</div>
</div>
<div class="body">
  <div class="sec-label">Client</div>
  <div class="client-name">${client.nickname||client.name||''}</div>
  <div class="client-detail">${client.address||''}${client.city?', '+client.city:''}</div>

  <div class="detail-grid">
    <div class="detail-item"><strong>Service Type</strong>${SERVICE_LABELS[form.service_type]||form.service_type}</div>
    <div class="detail-item"><strong>Frequency</strong>${FREQ_LABELS[form.frequency]||form.frequency}</div>
    <div class="detail-item"><strong>Rate</strong>$${parseFloat(form.rate||0).toFixed(2)} ${form.rate_type === 'hourly' ? 'per hour' : 'per visit'}</div>
  </div>

  ${form.scope_notes ? `<div class="scope-box light-pink">
    <div class="scope-label">Scope of Work</div>
    <div class="scope-text">${form.scope_notes}</div>
  </div>` : ''}

  <div class="terms-section">
    <div class="terms-title">Terms &amp; Conditions</div>
    ${TERMS.map(([title,text]) => `<div class="term-item"><div class="term-title">${title}</div><div class="term-text">${text}</div></div>`).join('')}
  </div>

  <div class="sign-section">
    <div class="sign-row">
      <div class="sign-field">
        <div class="sign-line"></div>
        <div class="sign-field-label">Printed Name</div>
      </div>
      <div class="sign-field">
        <div class="sign-line"></div>
        <div class="sign-field-label">Date</div>
      </div>
    </div>
    <div class="sign-row">
      <div class="sign-field">
        <div class="sign-line"></div>
        <div class="sign-field-label">Signature</div>
      </div>
    </div>
  </div>

  <div class="footer">
    <div class="footer-main">Thank you for choosing Kitty's Cleaning Services! 🐈✨</div>
    <div class="footer-sub">Please sign and return this page, or take a photo and send it back to us.</div>
  </div>
</div>
<script>window.onload=()=>window.print()</script>
</body></html>`
  const win = window.open('','_blank')
  win.document.write(html)
  win.document.close()
}

export default function WorkOrder({ client, onClose }) {
  const [orders, setOrders] = useState([])
  const [selectedId, setSelectedId] = useState(null) // null = creating a new order
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [form, setForm] = useState(blankForm(client))

  const order = orders.find(o => o.id === selectedId) || null

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('work_orders')
        .select('*')
        .eq('client_id', client.id)
        .order('created_at', { ascending: false })
      const list = data || []
      setOrders(list)
      if (list.length) {
        selectOrder(list[0])
      }
      setLoading(false)
    }
    load()
  }, [client.id])

  function selectOrder(o) {
    setSelectedId(o.id)
    setForm({
      service_type: o.service_type || 'standard',
      frequency: o.frequency || client.frequency || 'biweekly',
      rate: o.rate ?? (client.default_rate || ''),
      rate_type: o.rate_type || 'flat',
      scope_notes: o.scope_notes || '',
      allow_social_media: o.allow_social_media !== false,
    })
  }

  function startNew() {
    setSelectedId(null)
    setForm(blankForm(client))
  }

  async function createOrder() {
    setSaving(true)
    const { error } = await supabase.from('work_orders').insert([{
      client_id: client.id,
      service_type: form.service_type,
      frequency: form.frequency,
      rate: parseFloat(form.rate) || null,
      rate_type: form.rate_type || 'flat',
      scope_notes: form.scope_notes,
      allow_social_media: form.allow_social_media,
      status: 'pending',
    }])
    if (error) { alert('Error: ' + error.message); setSaving(false); return }
    // Fetch the newly created order (handles RLS)
    const { data } = await supabase.from('work_orders').select('*')
      .eq('client_id', client.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (data) {
      setOrders(prev => [data, ...prev])
      setSelectedId(data.id)
    }
    setSaving(false)
  }

  async function updateOrder() {
    setSaving(true)
    await supabase.from('work_orders').update({
      service_type: form.service_type,
      frequency: form.frequency,
      rate: parseFloat(form.rate) || null,
      rate_type: form.rate_type || 'flat',
      scope_notes: form.scope_notes,
      allow_social_media: form.allow_social_media,
      status: 'pending',
      signed_at: null,
      signed_name: null,
    }).eq('id', order.id)
    const { data } = await supabase.from('work_orders').select('*').eq('id', order.id).maybeSingle()
    if (data) {
      setOrders(prev => prev.map(o => o.id === data.id ? data : o))
    }
    setSaving(false)
  }

  const signLink = order
    ? `https://sign.kittyscleaningservices.com/?workorder=${order.token}`
    : null

  function copyLink() {
    navigator.clipboard.writeText(signLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const isSigned = order?.status === 'signed'

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={s.head}>
          <div>
            <div style={s.title}>📋 Work Order — {client.nickname || client.name}</div>
            <div style={s.sub}>{client.address || 'No address on file'}</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#9aa0a6' }}>Loading...</div>
        ) : (
          <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>

            {/* Work order list / switcher */}
            {orders.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                {orders.map(o => (
                  <button
                    key={o.id}
                    onClick={() => selectOrder(o)}
                    style={{
                      ...s.orderChip,
                      ...(o.id === selectedId ? s.orderChipActive : {}),
                    }}
                  >
                    {o.status === 'signed' ? '✅' : '⏳'} {SERVICE_LABELS[o.service_type] || o.service_type}
                    <span style={{ opacity: 0.6, marginLeft: 4 }}>
                      {new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </button>
                ))}
                <button
                  onClick={startNew}
                  style={{ ...s.orderChip, ...(selectedId === null ? s.orderChipActive : {}), borderStyle: 'dashed' }}
                >
                  + New work order
                </button>
              </div>
            )}

            {/* Status banner */}
            {order && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 16,
                background: isSigned ? '#e8f5e9' : '#fff8e1',
                border: `1px solid ${isSigned ? '#a5d6a7' : '#fde68a'}`,
                display: 'flex', alignItems: 'center', gap: 10
              }}>
                <span style={{ fontSize: 18 }}>{isSigned ? '✅' : '⏳'}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: isSigned ? '#1b5e20' : '#854d0e' }}>
                    {isSigned ? `Signed by ${order.signed_name}` : 'Pending signature'}
                  </div>
                  <div style={{ fontSize: 11, color: '#80868b' }}>
                    {isSigned
                      ? `Signed on ${new Date(order.signed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                      : 'Send the link below for the client to sign'}
                  </div>
                </div>
              </div>
            )}

            {!order && (
              <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: '#f8f9fa', border: '1px solid #e3e5e8', fontSize: 12, color: '#5f6368' }}>
                Creating a new work order for {client.nickname || client.name}. This won't affect their existing work orders.
              </div>
            )}

            {/* Form fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <div style={s.label}>Service type</div>
                <select style={s.input} value={form.service_type} onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))}>
                  {Object.entries(SERVICE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <div style={s.label}>Frequency</div>
                <select style={s.input} value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}>
                  {Object.entries(FREQ_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                  <div style={s.label}>{form.rate_type === 'hourly' ? 'Rate (hourly rate)' : 'Rate (flat per visit)'}</div>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                    {[['flat', 'Flat / visit'], ['hourly', 'Por hora']].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setForm(f => ({ ...f, rate_type: v }))}
                        style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, cursor: 'pointer',
                          border: `1px solid ${(form.rate_type || 'flat') === v ? '#e8547a' : '#ddd'}`,
                          background: (form.rate_type || 'flat') === v ? '#e8547a' : '#fff',
                          color: (form.rate_type || 'flat') === v ? '#fff' : '#5f6368' }}>{l}</button>
                    ))}
                  </div>
                </div>
                <input style={s.input} type="number" value={form.rate} placeholder={client.default_rate || '0'}
                  onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <div style={s.label}>Scope notes (what's included / special instructions)</div>
                <textarea style={{ ...s.input, height: 64, resize: 'vertical' }}
                  placeholder="e.g. 3 bed / 2 bath. Includes kitchen, bathrooms, and common areas. Does not include garage or laundry room."
                  value={form.scope_notes} onChange={e => setForm(f => ({ ...f, scope_notes: e.target.value }))} />
              </div>
            </div>

            {/* Social media opt-out */}
            <div style={{ padding: '10px 14px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #e3e5e8', marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.allow_social_media}
                  onChange={e => setForm(f => ({ ...f, allow_social_media: e.target.checked }))}
                  style={{ marginTop: 2, flexShrink: 0, accentColor: '#e8547a' }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#202124' }}>✨ I'd like to support Kitty's on social media</div>
                  <div style={{ fontSize: 11, color: '#5f6368', marginTop: 2, lineHeight: 1.6 }}>
                    Optional — check this to allow us to share select before/after photos on our social media.
                    We will never photograph bedrooms, always blur personal information, and only share kitchens,
                    bathrooms, and common areas. Completely optional and you can change your mind anytime.
                  </div>
                </div>
              </label>
            </div>

            {/* Save / create button */}
            <button style={{ ...s.btn, background: '#e8547a', color: '#fff', marginBottom: 16, width: '100%' }}
              onClick={order ? updateOrder : createOrder} disabled={saving}>
              {saving ? 'Saving...' : order ? '💾 Update work order' : '✉️ Create work order & get link'}
            </button>

            {/* Link to send */}
            {order && (
              <div style={{ background: isSigned ? '#f0fdf4' : '#f8f9fa', borderRadius: 10, padding: '12px 14px', border: `1px solid ${isSigned ? '#a5d6a7' : '#e3e5e8'}` }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#5f6368', marginBottom: 6 }}>
                  {isSigned ? 'Signed work order link' : 'Send this link to the client to sign'}
                </div>
                <div style={{ fontSize: 11, color: '#0c447c', wordBreak: 'break-all', background: '#fff', padding: '6px 10px', borderRadius: 6, marginBottom: 8, border: '1px solid #e0e0e0' }}>
                  {signLink}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{ ...s.btn, background: copied ? '#1d9e75' : '#185fa5', color: '#fff', flex: 1 }} onClick={copyLink}>
                    {copied ? '✓ Copied!' : '📋 Copy link'}
                  </button>
                  <button style={{ ...s.btn, background: '#25D366', color: '#fff', flex: 1 }}
                    onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Hi! Please review and sign your Kitty's Cleaning Services work order here: ${signLink}`)}`)}>
                    📱 Send via WhatsApp
                  </button>
                </div>
                <button style={{ ...s.btn, background: '#fff', color: '#e8547a', border: '1px solid #e8547a', width: '100%', marginTop: 8 }}
                  onClick={() => generateWorkOrderPDF({ client, form })}>
                  🖨️ PDF para firmar a mano
                </button>
                {!isSigned && (
                  <div style={{ fontSize: 10, color: '#9aa0a6', marginTop: 8, textAlign: 'center' }}>
                    You can also text or email this link. The client signs directly on their phone — or print the PDF above for anyone who prefers signing on paper.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal: { background: '#fff', borderRadius: 16, boxShadow: '0 16px 48px rgba(0,0,0,0.2)', width: '92vw', maxWidth: 560, maxHeight: '88vh', display: 'flex', flexDirection: 'column' },
  head: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #e3e5e8', flexShrink: 0 },
  title: { fontSize: 15, fontWeight: 700, color: '#202124' },
  sub: { fontSize: 11, color: '#80868b', marginTop: 2 },
  closeBtn: { fontSize: 20, border: 'none', background: 'none', cursor: 'pointer', color: '#9aa0a6', lineHeight: 1, padding: 0 },
  label: { fontSize: 10, color: '#80868b', marginBottom: 4, fontWeight: 500 },
  input: { width: '100%', padding: '7px 9px', border: '1px solid #e3e5e8', borderRadius: 8, fontSize: 12, background: '#f8f9fa', color: '#202124', fontFamily: 'inherit' },
  btn: { padding: '8px 14px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  orderChip: { padding: '5px 10px', borderRadius: 999, border: '1px solid #e3e5e8', background: '#f8f9fa', color: '#5f6368', fontSize: 11, cursor: 'pointer', fontWeight: 500 },
  orderChipActive: { background: '#e8547a', borderColor: '#e8547a', color: '#fff' },
}
